import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { Button, RawInput, Text } from '@payaca/ambrosia';
import {
  requestGetCustomer,
  requestGetListedCustomersPage,
} from '@payaca/store/customer/customerActions';
import { useCustomer, useSelector } from '@payaca/store/hooks/appState';
import { ListedCustomer } from '@payaca/types/listedCustomerTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

type Props = {
  selectedCustomerId?: number;
  onChange: (selectedCustomerId?: number) => void;
};

/**
 * Customer filter styled as a pill dropdown to match the other list filters.
 * Keeps server-side search (customers are paged from the backend by term)
 * rather than filtering a fixed in-memory list.
 */
const CustomerFilterDropdown = ({ selectedCustomerId, onChange }: Props) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');

  const selectedCustomer = useCustomer(selectedCustomerId);
  const isGettingListedCustomersPage = useSelector(
    (state) => state.customer.isGettingListedCustomersPage
  );
  const listedCustomers: ListedCustomer[] = useSelector(
    (state) => state.customer?.listedCustomersPage?.items || []
  );

  // Load the selected customer's details so the pill can show its name.
  useEffect(() => {
    if (selectedCustomerId) {
      dispatch(requestGetCustomer(selectedCustomerId));
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- only refetch when the id changes
  }, [selectedCustomerId]);

  // Debounced server search as the user types.
  useEffect(() => {
    const handle = setTimeout(() => {
      dispatch(
        requestGetListedCustomersPage({
          pageSize: 20,
          pageNumber: 1,
          searchTerm: searchTerm || undefined,
        })
      );
    }, 300);
    return () => clearTimeout(handle);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- dispatch is stable
  }, [searchTerm]);

  return (
    <Popover className="relative">
      <PopoverButton as={Button} variant="secondary" size="sm">
        <UntitledIcon name="filter-lines.3" className="h-3.5 w-3.5" />
        {selectedCustomer?.name || 'Customer'}
        {!!selectedCustomerId && (
          <UntitledIcon
            name="x.3"
            className="size-4"
            onClick={(e) => {
              e.stopPropagation();
              onChange();
            }}
          />
        )}
      </PopoverButton>

      <PopoverPanel
        anchor={{ to: 'bottom end', gap: 4 }}
        portal
        className="z-dropdown min-w-44 rounded-lg border border-gray-200 bg-white p-2 shadow-md"
      >
        {({ close }) => (
          <>
            <RawInput
              type="search"
              placeholder="Search..."
              sizeVariant="sm"
              autoComplete="off"
              className="mb-2"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="max-h-64 overflow-y-auto">
              <CustomerOptions
                customers={listedCustomers}
                isLoading={isGettingListedCustomersPage}
                selectedCustomerId={selectedCustomerId}
                onSelect={(customerId) => {
                  onChange(customerId);
                  close();
                }}
              />
            </div>
          </>
        )}
      </PopoverPanel>
    </Popover>
  );
};

const CustomerOptions = ({
  customers,
  isLoading,
  selectedCustomerId,
  onSelect,
}: {
  customers: ListedCustomer[];
  isLoading: boolean;
  selectedCustomerId?: number;
  onSelect: (customerId: number) => void;
}) => {
  if (isLoading && !customers.length) {
    return (
      <div className="px-2 py-1.5">
        <Text colour="muted">Loading…</Text>
      </div>
    );
  }

  if (!customers.length) {
    return (
      <div className="px-2 py-1.5">
        <Text colour="muted">No customers found</Text>
      </div>
    );
  }

  return (
    <>
      {customers.map((customer) => (
        <button
          key={customer.customerId}
          type="button"
          className={clstx(
            'flex w-full items-center rounded-md bg-transparent px-2 py-1.5 text-start hover:bg-gray-100',
            customer.customerId === selectedCustomerId && 'bg-gray-100'
          )}
          onClick={() => onSelect(customer.customerId)}
        >
          <Text as="span">{customer.customerName}</Text>
        </button>
      ))}
    </>
  );
};

export default CustomerFilterDropdown;
